/**
 * @file formatCurrencyShort.ts
 * @description Abbreviated currency rendering (`€1K`, `$2M`). The `"en-US"`
 * literals in this file are DELIBERATE and must not be localized.
 *
 * ## Read this before "finishing the localization" (SE-8058)
 *
 * {@link module:utils/intlFormat} says call sites never write a locale
 * literal. The two `"en-US"` literals below break that rule ON PURPOSE, and
 * SE-8058 is what sanctions them. They are not an oversight left behind by
 * SE-7966 — SE-7966 was told to leave currency alone and did so.
 *
 * **This is not a blanket amnesty for locale literals.** Five others survive
 * under `src/` — in `formatNumberWithCommas.ts`, `playerFormatting.ts`,
 * `formatRelativeTimestamp.ts`, `generatePlayerReportPdf.ts` and
 * `player-detail/utils/index.ts`. SE-8058 rules on the currency pair only and
 * says nothing about those; do not read this header as covering them.
 *
 * The result is a screen that is half-localized, and that is accepted, not
 * pending: on Squad Planning a German user sees `1.234` minutes (locale-aware,
 * SE-7966) beside a `€1K` wage (fixed, here) in the same row. Cody ruled on
 * 2026-09-04 that the currency symbol stays FIXED — not locale-driven, not
 * account-driven. That comment deliberately left PLACEMENT open; placement was
 * confirmed fixed as well on 2026-09-18. The question is settled; SE-8058 is
 * the record of it. Do not reopen it in code.
 *
 * ## Localizing this file is also a bug, not just a different look
 *
 * Separately from the product decision, {@link getCurrencySymbol} derives a
 * symbol by formatting `0` and deleting the digits. That trick only holds for
 * the shape `en-US` produces. Measured under SE-8058:
 *
 * - **Some locales rename the symbol rather than move it.** `USD` is `"$"` in
 *   `en-US`, but `"$US"` in `fr-FR`, `"US$"` in `en-CA` and `pt-BR`, and
 *   `"USD"` in `es-MX`. Values would render `$US5K`.
 * - **`/\d/g` only matches ASCII digits.** In `ar-EG`, `bn-BD`, `fa-IR` and
 *   Devanagari-numeral `hi-IN`, the zero is not stripped and survives into the
 *   returned symbol, so every abbreviated figure would carry a stray numeral
 *   (`٠` in `ar-EG`).
 *
 * The trap that makes this worth spelling out: **`de-DE` is a false-negative
 * test locale.** It is the obvious one to try, and it returned glyphs
 * identical to `en-US` for all eight currencies measured (USD, EUR, GBP, BRL,
 * JPY, CHF, SEK, INR) — so a developer who localizes this and checks their
 * work in German sees nothing change and ships it.
 *
 * `src/utils/__tests__/formatCurrencyShort.test.ts` pins both literals, so
 * this is enforced rather than merely written down.
 * @module utils/formatCurrencyShort
 */
import { CurrencyCode } from "@/generated/graphql";

/**
 * Retrieves the currency symbol for a given currency code.
 *
 * Deliberately fixed to `en-US`; see the file header. The digit-stripping
 * below assumes the string shape `en-US` produces and breaks under locales
 * that rename the symbol or use non-ASCII numerals.
 * @param currencyCode - The ISO 4217 currency code
 * @returns The currency symbol (e.g., "$", "€", "£")
 */
function getCurrencySymbol(currencyCode: string): string {
  // Explicitly handle common currencies to ensure correct symbol is used
  if (currencyCode === "EUR") {
    return "€";
  }

  return (
    (0)
      // SE-8058: fixed locale, by product decision AND because the digit strip
      // below only holds for this shape. Do not thread a user locale in here.
      .toLocaleString("en-US", {
        style: "currency",
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      })
      .replace(/\d/g, "")
      .trim()
  );
}

/**
 * Magnitude beyond which even a trillion-suffixed value would still spill
 * three-plus digits before the "T" (e.g. a data anomaly in the quadrillions).
 * Past this point there is no short-scale suffix that stays bounded, so
 * {@link formatCurrencyShort} falls back to exponential notation instead of
 * growing the digit count without limit (SE-8031).
 */
const EXPONENTIAL_FALLBACK_THRESHOLD = 1_000_000_000_000_000;

/**
 * Formats a numeric value as abbreviated currency (K, M, B, T suffixes).
 *
 * Every tier below the exponential fallback ROUNDS to the nearest whole
 * unit. The two largest bands used to truncate (`Math.floor`) while the two
 * smallest rounded, which made the function disagree with itself across a
 * threshold it alone chose: €1.9M drew "€2M" but €101.9M drew "€101M", and
 * truncation is asymmetric about zero, so the same magnitude formatted
 * differently by sign (−€341,999,995 floored to "€-342M" while its positive
 * twin floored to "€341M"). SE-5760: a €5 wage impact against a
 * €342,000,000 bill left an adjusted bill of €341,999,995, which floors to
 * "€341M" — so the Yearly Wage Bill card showed headline €341M, delta −€5
 * and baseline €342M, three numbers that cannot all be true. The card's own
 * arithmetic was exact; the truncation here is what made it read as broken.
 *
 * Every tier also divides down to at most three integer digits before its
 * suffix (e.g. "€999B", "€999T"), so the result has a bounded width a
 * fixed-width tile can always fit — the overflow this guards against is
 * SE-8031, where an unusually large wage bill / deviation value
 * (billions-plus, sometimes trillions-plus from upstream data) rendered as
 * a long unabbreviated digit run and broke card layout.
 * @param value - The numeric value to format
 * @param currencyCode - The currency code from GraphQL schema
 * @returns Abbreviated currency string (e.g., "$2M", "€500K", "$2B", "€6T")
 */
export default function formatCurrencyShort(
  value: number,
  currencyCode: CurrencyCode
): string {
  const absValue = Math.abs(value);
  const currencySymbol = getCurrencySymbol(currencyCode);

  if (absValue >= EXPONENTIAL_FALLBACK_THRESHOLD) {
    // No named short-scale suffix stays bounded beyond this magnitude, so
    // fall back to a fixed-width exponential form rather than letting the
    // digit count grow without limit.
    return `${currencySymbol}${value.toExponential(1)}`;
  } else if (absValue >= 1_000_000_000_000) {
    return `${currencySymbol}${(value / 1_000_000_000_000).toFixed(0)}T`;
  } else if (absValue >= 1_000_000_000) {
    return `${currencySymbol}${(value / 1_000_000_000).toFixed(0)}B`;
  } else if (absValue >= 100_000_000) {
    return `${currencySymbol}${(value / 1_000_000).toFixed(0)}M`;
  } else if (absValue >= 1_000_000) {
    return `${currencySymbol}${(value / 1_000_000).toFixed(0)}M`;
  } else if (absValue >= 1_000) {
    return `${currencySymbol}${(value / 1_000).toFixed(0)}K`;
  }

  // SE-8058: fixed locale, by product decision — see the file header. This is
  // the only branch whose NUMBER could vary by locale (`€999.55` vs
  // `999,55 €`); the abbreviated bands above use `toFixed(0)` and carry no
  // separator. Every band still takes its symbol from `getCurrencySymbol`,
  // so the symbol is fixed everywhere, not just here.
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 0,
  });
}

/**
 * Formats a min/max Gemini player valuation range as abbreviated currency.
 * @param min - Lower bound of the valuation range
 * @param max - Upper bound of the valuation range
 * @param currencyCode - ISO 4217 currency code (defaults to EUR since Gemini valuations are euro-denominated)
 * @returns Formatted range string like "€1M - €5M", or null when either value is missing so callers can apply their own fallback
 */
export function formatGeminiValuationRange(
  min: number | null | undefined,
  max: number | null | undefined,
  currencyCode: CurrencyCode = CurrencyCode.Eur
): string | null {
  return min != null && max != null
    ? `${formatCurrencyShort(min, currencyCode)} - ${formatCurrencyShort(max, currencyCode)}`
    : null;
}
